/**
 * Какая реклама привела человека — ОДНА запись, у которой теперь два потребителя.
 *
 * Раньше это жило внутри analytics.ts: читалось из адреса один раз при загрузке и цеплялось к
 * `page_viewed`. Этого хватало ровно на один вопрос — «докуда доходит трафик кампании» — и не
 * хватало ни на один следующий. Событие живёт у вендора; КТО пришёл, какую компанию подключил и что
 * про себя рассказал — знает наш сервер; связать одно с другим было нечем, и вопрос «насколько
 * целевых людей приводит эта кампания» оставался без ответа при работающей рекламе.
 *
 * ДВА ОТЛИЧИЯ ОТ ТОЙ ВЕРСИИ, и оба вынужденные.
 *
 * ПАМЯТЬ. `utm_*` живут только в адресе лендинга: роутер переписывает адрес на первом же переходе,
 * а возврат с внешней оплаты — это полная перезагрузка, после которой в памяти не оставалось ничего.
 * Первое касание кладётся в localStorage и читается оттуда, поэтому доживает и до регистрации, и до
 * возврата с чекаута.
 *
 * ДЛИНА. Значение режется до 200 знаков, было 120. Имена кампаний Меты
 * («INC_WEB_TopGeo12-excl2_FB_VS_noSF_B2B-v12en_Usual_Checkout_…_020926_ev13») занимают 121…129
 * знаков — прежний предел отрезал хвост с датой запуска, и два разных запуска одной кампании
 * складывались в одно ведро.
 *
 * АЛЛОУ-ЛИСТ, а не весь query string. Эти ключи описывают ОБЪЯВЛЕНИЕ, а не человека; `/thank-you`
 * несёт параметры заказа, и любой следующий экран может нести ещё что-нибудь, так что копирование
 * строки запроса целиком — это способ выпустить контакт наружу по недосмотру. Referrer урезан до
 * хоста по той же причине: полный адрес может нести чужой поисковый запрос.
 */

import { StorageKeys, storageService } from '@/utils/localStorageService'

/** Ровно те ключи, что описывают объявление. Расширять — только здесь. */
const AD_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'fbclid',
] as const

/** См. комментарий выше: 120 резал имена кампаний Меты по живому. */
const MAX_VALUE = 200

export type Acquisition = { [K in (typeof AD_KEYS)[number]]: string | null } & {
  /** Только хост. Полный referrer может нести чужой поисковый запрос. */
  referrer_host: string | null
  /** Когда это касание случилось — иначе «первое» нечем подтвердить в отчёте. */
  landing_at: string | null
}

const EMPTY: Acquisition = {
  utm_source: null,
  utm_medium: null,
  utm_campaign: null,
  utm_content: null,
  utm_term: null,
  gclid: null,
  fbclid: null,
  referrer_host: null,
  landing_at: null,
}

/** Есть ли в записи хоть одна метка объявления. Referrer сюда не считается: он есть у кого угодно. */
const hasAd = (value: Acquisition): boolean => AD_KEYS.some((key) => value[key])

const readHere = (): Acquisition => {
  if (typeof window === 'undefined') return EMPTY
  const here: Acquisition = { ...EMPTY, landing_at: new Date().toISOString() }
  try {
    const query = new URLSearchParams(window.location.search)
    for (const key of AD_KEYS) {
      const value = query.get(key)
      if (value) here[key] = value.slice(0, MAX_VALUE)
    }
    if (document.referrer) here.referrer_host = new URL(document.referrer).host
  } catch {
    // Кривой адрес или referrer не стоит того, чтобы бросать исключение внутри аналитики.
  }
  return here
}

/**
 * ПЕРВОЕ КАСАНИЕ, а не последнее: за человека заплатило то объявление, по которому он пришёл
 * впервые, а не то, на которое он вернулся через неделю уже зная про нас.
 *
 * Ровно одно исключение — сохранённая запись БЕЗ метки объявления. Заход мог начаться с прямого
 * адреса, из письма или с закладки, и тогда первый клик по рекламе И ЕСТЬ первое касание, которое
 * стоит денег; без этого исключения пустая запись навсегда съедала бы кампанию, пришедшую следом.
 */
const remember = (): Acquisition => {
  const stored = storageService.getItem<Acquisition>(StorageKeys.acquisition)
  const here = readHere()
  if (stored && (hasAd(stored) || !hasAd(here))) return stored
  storageService.setItem(StorageKeys.acquisition, here)
  return here
}

/* Один раз за загрузку страницы, как и было: адрес читается до того, как роутер его перепишет. */
const FIRST_TOUCH: Acquisition = remember()

/** Для сервера — запись целиком, включая пустые поля: «метки не было» это ответ, а не отсутствие. */
export const acquisition = (): Acquisition => FIRST_TOUCH

/**
 * Для Amplitude — только метки объявления и хост, и только заполненные. Форма события остаётся той
 * же, что была: гарантированные `null` в каждом `page_viewed` — это шум в отчёте, которого никто не
 * просил, а `landing_at` вендору не нужен вовсе, у него есть собственное время события.
 *
 * Одно поведение всё же изменилось, и в лучшую сторону: раньше метка бралась из АДРЕСА ЭТОЙ
 * ЗАГРУЗКИ и терялась на любой перезагрузке, теперь это первое касание — так что события после
 * возврата с оплаты больше не выпадают из отчёта по кампании.
 */
export const acquisitionProps = (): Record<string, string> => {
  const props: Record<string, string> = {}
  for (const key of [...AD_KEYS, 'referrer_host'] as const) {
    const value = FIRST_TOUCH[key]
    if (value) props[key] = value
  }
  return props
}
